/**
 * settingsVisibility — which settings apply to THIS account.
 *
 * SETTINGS-SPLIT-1. The owner's complaint, in his words: the settings page is
 * "a long list of a bunch of random things that did not even need to be made
 * into tabs, it is like backend stuff then a ton of specific stuff because we
 * were initially building the software to be a CRM for home service
 * businesses."
 *
 * Twenty four entries sit in one flat list. **Field crew payroll and door
 * knocking, in the same list as change your password.** Measured: six of fifty
 * accounts touch any of the four trade specific entries; forty four do not.
 *
 * MOVED IS NOT DELETED, and that distinction is the whole wave. Nothing here
 * removes a route, a page, or a stored value. This module answers one question —
 * does this entry belong on this account's list — and every entry it leaves off
 * is still reachable, still stored, and reappears the moment the account starts
 * using the thing.
 *
 * PURE AND NOT MIRRORED, so mutation testing can run on it.
 */

/** Where an entry belongs, and therefore what decides whether it shows. */
export type Bucket = 'platform' | 'shelf' | 'block' | 'trade' | 'backend'

/** The trades a business can actually be doing. */
export type Trade = 'canvassing' | 'payroll' | 'territory'

export interface SettingsEntry {
  id: string
  label: string
  bucket: Bucket
  /** For `block`: the menu module it belongs to. */
  module?: string
  /** For `trade`: the trade that has to be happening. */
  trade?: Trade
}

/**
 * WHAT THIS ACCOUNT ACTUALLY DOES.
 *
 * Judged by what the business has DONE, never by a flag somebody set. A flag
 * says what a person once intended; a canvass pin says somebody knocked on a
 * door. The product has been wrong about this before by trusting a default row:
 * all fifty accounts have a `pay_period_settings` row saying `biweekly` and only
 * two have ever been edited, so "has a row" would have shown payroll to
 * everybody — exactly the bug.
 */
export interface AccountActivity {
  /** Menu modules this account has. */
  modules: readonly string[]
  canvassPins: number
  payPeriods: number
  paychecks: number
  serviceAreas: number
}

/** Is this account doing this trade? */
export function doesTrade(activity: AccountActivity, trade: Trade): boolean {
  switch (trade) {
    case 'canvassing':
      return activity.canvassPins > 0
    case 'payroll':
      // Either half counts. A business that has run a period but not yet cut a
      // cheque is still doing payroll, and one with cheques but no period rows
      // has clearly been paying people.
      return activity.payPeriods > 0 || activity.paychecks > 0
    case 'territory':
      return activity.serviceAreas > 0
  }
}

/**
 * DOES THIS ENTRY BELONG ON THIS ACCOUNT'S SETTINGS LIST?
 *
 * Backend is checked first and unconditionally: a merchant never sees it, and no
 * amount of activity changes that.
 */
export function showsInSettings(entry: SettingsEntry, activity: AccountActivity): boolean {
  switch (entry.bucket) {
    case 'backend':
      return false
    case 'platform':
    case 'shelf':
      return true
    case 'block':
      // The shelf lane owns which modules an account has. This reads the answer
      // rather than deciding it — the declared link, not a second shelf.
      return entry.module === undefined || activity.modules.includes(entry.module)
    case 'trade':
      return entry.trade !== undefined && doesTrade(activity, entry.trade)
  }
}

/** The list a merchant sees, in declaration order. */
export function visibleSettings(
  entries: readonly SettingsEntry[],
  activity: AccountActivity,
): SettingsEntry[] {
  return entries.filter((e) => showsInSettings(e, activity))
}

/**
 * WHY AN ENTRY IS NOT SHOWN, in the merchant's words.
 *
 * Not for a tooltip — for the honest answer when somebody asks where a thing
 * went, and for the report. An entry that is merely hidden must never be
 * describable as removed.
 */
export function whyHidden(entry: SettingsEntry, activity: AccountActivity): string | null {
  if (showsInSettings(entry, activity)) return null
  // Only these three can be hidden. Platform and shelf always show, so they
  // returned null on the line above — a `default` arm here was unreachable and
  // mutation proved it, so it is gone rather than pinned.
  if (entry.bucket === 'backend') {
    return 'This is ours, not yours. It is not part of your settings.'
  }
  if (entry.bucket === 'block') {
    return `This belongs to ${entry.module}, which is not one of your sections. Turn that on and it comes back.`
  }
  return 'Nothing on this account uses this yet. It appears by itself the moment something does.'
}

/**
 * THE PROMISE THIS WAVE MAKES, as a function.
 *
 * Hiding an entry must not touch what it was set to. This takes the stored
 * values before and after and answers whether anything was lost — which is what
 * guard 3 asserts, and what makes "moved is not deleted" checkable rather than
 * a claim in a document.
 */
export function valuesPreserved(
  before: Readonly<Record<string, unknown>>,
  after: Readonly<Record<string, unknown>>,
): boolean {
  for (const [key, value] of Object.entries(before)) {
    // A missing key stringifies to `undefined` and fails this comparison, so a
    // separate `key in after` check stood here and could never decide anything.
    // Mutation found it unkillable; deleted rather than pinned.
    if (JSON.stringify(after[key]) !== JSON.stringify(value)) return false
  }
  return true
}

/** Every entry that is hidden but still reachable — the "moved, not deleted" set. */
export function hiddenButReachable(
  entries: readonly SettingsEntry[],
  activity: AccountActivity,
): SettingsEntry[] {
  return entries.filter((e) => !showsInSettings(e, activity) && e.bucket !== 'backend')
}
